import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';

import type { PartyType, SelectableParty } from '@clm/shared-types';

import { ApiError } from '../api/errors';
import { classifyApiError } from '../api/problem';
import { searchSelectableParties } from '../api/upload';
import { loginRequest, msalInstance } from '../auth/msal';

/**
 * The client picker — everywhere a party is chosen (CLM-68).
 *
 * A `<select>` capped at 200 rows hid every party past the C's, which is how
 * Sainsbury went missing from *Add a contract* on 11 Sep 2026. This is a
 * type-ahead combobox over the **scoped** selectable-party list: from two
 * characters, after 250 ms, it asks `upload-options` with `party_search` and a
 * `page`, lists what came back in server order (the exact key promoted), and
 * offers *Load more* while the server says there is a next page.
 *
 * ## FR-14 in the component
 *
 * The value handed back is never the typed text. `onChange` receives the whole
 * {@link SelectableParty} the API offered — or `null` on clear — so the surface
 * keeps `processing_restricted` without a second lookup and can never file
 * against a key the caller did not see. The typed term is a *search*; the
 * surface's value stays the last picked key until a new pick or a clear.
 *
 * ## Rule 11
 *
 * No `fetch` here: the one request is `searchSelectableParties` in `src/api`,
 * which carries the bearer token and parses the response through the contract.
 */

export interface PartyPickerProps {
  /** The input's id; the surface's `<label htmlFor>` points at it. */
  readonly id: string;
  /** Accessible name when the surface has no `<label>` (table cells). */
  readonly label?: string;
  /** A `party_key` the API offered, or `''`. Never free text. */
  readonly value: string;
  readonly onChange: (party: SelectableParty | null) => void;
  readonly disabled?: boolean;
  /** Renders the picked party as text — a filed row, not an editable one. */
  readonly readOnly?: boolean;
  /**
   * Parties already known to the surface (the options response, the bundle
   * plan), used to render `value` without a request. The picker never lists
   * them without a term.
   */
  readonly initialParties?: readonly SelectableParty[];
  /** Placeholder while nothing is picked. */
  readonly emptyLabel?: string;
  /**
   * CLM-78 (B3) — narrow the search to one `party_type`. `VendorPicker` wraps
   * this component with `'Vendor'`; omitted, the request is unchanged.
   */
  readonly partyType?: PartyType;
  /** Copy for the empty result — `VendorPicker` says "No vendor matches". */
  readonly noMatchLabel?: string;
}

/** Fewer characters than this never leave the browser. */
const PARTY_SEARCH_MIN_CHARS = 2;
/** Keystrokes closer together than this collapse into one request. */
const PARTY_SEARCH_DEBOUNCE_MS = 250;
/**
 * The contract's `party_search` maximum. A longer paste is cut to this before
 * the request, so the search still runs instead of failing the schema parse and
 * surfacing as "did not reach the API".
 */
const PARTY_SEARCH_MAX_CHARS = 200;

type SearchStatus =
  | { readonly kind: 'idle' }
  | { readonly kind: 'searching' }
  | { readonly kind: 'done' }
  | { readonly kind: 'error'; readonly detail: string };

function partyLabel(party: SelectableParty): string {
  return `${party.display_name} (${party.party_key})`;
}

/**
 * The exact-key row first, whatever page it arrived on — the server promotes
 * it on page one; this covers a match appended from a later page.
 */
function pinExactKey(parties: readonly SelectableParty[], term: string): readonly SelectableParty[] {
  const key = term.trim().toUpperCase();
  const index = parties.findIndex((party) => party.party_key === key);
  if (index <= 0) return parties;
  const pinned = parties[index];
  if (pinned === undefined) return parties;
  return [pinned, ...parties.slice(0, index), ...parties.slice(index + 1)];
}

function appendDistinct(
  current: readonly SelectableParty[],
  incoming: readonly SelectableParty[],
): readonly SelectableParty[] {
  const seen = new Set(current.map((party) => party.party_key));
  return [...current, ...incoming.filter((party) => !seen.has(party.party_key))];
}

function describeFailure(error: unknown): string {
  const kind = classifyApiError(error);
  if (kind === 'forbidden') return 'The client list could not be read with your role.';
  if (error instanceof ApiError) return error.message;
  return 'The client search did not reach the API.';
}

export function PartyPicker({
  id,
  label,
  value,
  onChange,
  disabled = false,
  readOnly = false,
  initialParties = [],
  emptyLabel = 'Search clients by name or key…',
  partyType,
  noMatchLabel = 'No client matches',
}: PartyPickerProps) {
  const listId = useId();
  const [term, setTerm] = useState('');
  const [editing, setEditing] = useState(false);
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<readonly SelectableParty[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [status, setStatus] = useState<SearchStatus>({ kind: 'idle' });
  const [active, setActive] = useState(-1);
  /** The last party this picker handed out, so `value` renders without `initialParties`. */
  const [lastPicked, setLastPicked] = useState<SelectableParty | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const picked: SelectableParty | null =
    value === ''
      ? null
      : lastPicked !== null && lastPicked.party_key === value
        ? lastPicked
        : (initialParties.find((party) => party.party_key === value) ??
          results.find((party) => party.party_key === value) ??
          null);
  const pickedText = picked === null ? value : partyLabel(picked);

  const trimmed = term.trim().slice(0, PARTY_SEARCH_MAX_CHARS);
  const searchable = trimmed.length >= PARTY_SEARCH_MIN_CHARS;

  /* The search: debounced, aborting the previous request, restarting from page 1. */
  useEffect(() => {
    if (!editing || !searchable) {
      abortRef.current?.abort();
      abortRef.current = null;
      setResults([]);
      setNextCursor(null);
      setStatus({ kind: 'idle' });
      setActive(-1);
      return;
    }
    const timer = window.setTimeout(() => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setStatus({ kind: 'searching' });
      void (async () => {
        try {
          const page = await searchSelectableParties(trimmed, undefined, controller.signal, partyType);
          if (controller.signal.aborted) return;
          setResults(page.parties);
          setNextCursor(page.parties_page.next_cursor);
          setStatus({ kind: 'done' });
          setActive(-1);
        } catch (error) {
          if (controller.signal.aborted) return;
          if (error instanceof ApiError && error.isUnauthenticated) {
            // Re-authenticate the way the surfaces do: a redirect, never a popup.
            void msalInstance.loginRedirect(loginRequest);
            return;
          }
          // Previous options are kept; the failure is said inline.
          setStatus({ kind: 'error', detail: describeFailure(error) });
        }
      })();
    }, PARTY_SEARCH_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [editing, searchable, trimmed, partyType]);

  /* Abort whatever is in flight on unmount. */
  useEffect(() => () => abortRef.current?.abort(), []);

  function loadMore(): void {
    if (nextCursor === null || status.kind === 'searching') return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const cursor = nextCursor;
    setStatus({ kind: 'searching' });
    void (async () => {
      try {
        const page = await searchSelectableParties(trimmed, cursor, controller.signal, partyType);
        if (controller.signal.aborted) return;
        setResults((current) => appendDistinct(current, page.parties));
        setNextCursor(page.parties_page.next_cursor);
        setStatus({ kind: 'done' });
      } catch (error) {
        if (controller.signal.aborted) return;
        if (error instanceof ApiError && error.isUnauthenticated) {
          void msalInstance.loginRedirect(loginRequest);
          return;
        }
        setStatus({ kind: 'error', detail: describeFailure(error) });
      }
    })();
  }

  function pick(party: SelectableParty): void {
    setLastPicked(party);
    onChange(party);
    setTerm('');
    setEditing(false);
    setOpen(false);
    setActive(-1);
  }

  function clear(): void {
    setLastPicked(null);
    onChange(null);
    setTerm('');
    setEditing(false);
    setOpen(false);
    setActive(-1);
    inputRef.current?.focus();
  }

  function close(): void {
    setEditing(false);
    setOpen(false);
    setActive(-1);
    setTerm('');
  }

  if (readOnly) {
    return (
      <span className="pp-ro" aria-label={label} data-party-key={value}>
        {picked === null ? (value === '' ? '—' : value) : (
          <>
            {picked.display_name} <span className="mut">({picked.party_key})</span>
          </>
        )}
      </span>
    );
  }

  const shown = pinExactKey(results, trimmed);
  const hasLoadMore = nextCursor !== null && status.kind !== 'searching';
  const rowCount = shown.length + (hasLoadMore ? 1 : 0);
  const showList = open && editing;
  const optionId = (index: number): string => `${listId}-opt-${String(index)}`;
  const activeId = active >= 0 && active < rowCount ? optionId(active) : undefined;

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!editing) setEditing(true);
      setOpen(true);
      if (rowCount === 0) return;
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActive((current) => (current < 0 ? (step > 0 ? 0 : rowCount - 1) : (current + step + rowCount) % rowCount));
      return;
    }
    if (event.key === 'Enter') {
      if (!showList || active < 0) return;
      event.preventDefault();
      if (active < shown.length) {
        const party = shown[active];
        if (party !== undefined) pick(party);
      } else if (hasLoadMore) {
        loadMore();
      }
      return;
    }
    if (event.key === 'Escape') {
      if (showList) event.preventDefault();
      close();
      return;
    }
    if (event.key === 'Tab') {
      close();
    }
  }

  return (
    <div className="pp" onBlur={(event) => {
      /* Focus moving inside the popup (Load more) is not a leave. */
      if (event.currentTarget.contains(event.relatedTarget)) return;
      close();
    }}>
      <div className="pp-control">
        <input
          ref={inputRef}
          id={id}
          type="text"
          role="combobox"
          className="pp-input"
          aria-label={label}
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={activeId}
          aria-busy={status.kind === 'searching'}
          autoComplete="off"
          spellCheck={false}
          data-party-key={value}
          disabled={disabled}
          placeholder={picked === null ? emptyLabel : undefined}
          value={editing ? term : pickedText}
          onFocus={() => {
            if (disabled) return;
            setEditing(true);
            setOpen(true);
          }}
          onChange={(event) => {
            setEditing(true);
            setOpen(true);
            setTerm(event.target.value);
          }}
          onKeyDown={onKeyDown}
        />
        {value !== '' && !disabled ? (
          <button
            type="button"
            className="pp-clear"
            aria-label={label === undefined ? 'Clear client' : `Clear ${label.replace(/^Client/, 'client')}`}
            onMouseDown={(event) => {
              event.preventDefault();
            }}
            onClick={clear}
          >
            ×
          </button>
        ) : null}
      </div>

      <ul
        id={listId}
        role="listbox"
        className="pp-list"
        aria-label={label === undefined ? 'Clients' : `${label} options`}
        hidden={!showList}
      >
        {!searchable ? (
          <li className="pp-hint mut" role="presentation">
            Type at least two characters
          </li>
        ) : null}
        {status.kind === 'searching' ? (
          <li className="pp-hint mut" role="status" aria-busy="true">
            Searching…
          </li>
        ) : null}
        {status.kind === 'error' ? (
          <li className="pp-err" role="alert">
            {status.detail}
          </li>
        ) : null}
        {searchable && status.kind === 'done' && shown.length === 0 ? (
          <li className="pp-empty mut" role="option" aria-selected={false} aria-disabled="true">
            {noMatchLabel}
          </li>
        ) : null}
        {shown.map((party, index) => (
          <li
            key={party.party_key}
            id={optionId(index)}
            role="option"
            className={`pp-opt${index === active ? ' active' : ''}`}
            aria-selected={index === active}
            data-party-key={party.party_key}
            onMouseDown={(event) => {
              /* Pick on mousedown so the input's blur does not close the list first. */
              event.preventDefault();
              pick(party);
            }}
            onMouseEnter={() => {
              setActive(index);
            }}
          >
            <span className="pp-name">
              {party.display_name}
              {/* CLM-114 (R-114-3 (a)) — the search matched an active party alias, not the display name:
                  say which, so a person who typed a trading name sees why a differently named party came up. */}
              {party.matched_alias === null || party.matched_alias === undefined ? null : (
                <span className="pp-aka">also known as {party.matched_alias}</span>
              )}
              {/* CLM-126 — present only on a nearest-match candidate (never an ordinary search
                  result): the trigram score, so a suggestion reads as one even inside the dropdown. */}
              {party.suggestion_score === null || party.suggestion_score === undefined ? null : (
                <span className="pp-score">~{Math.round(party.suggestion_score * 100)}% match</span>
              )}
            </span>{' '}
            <span className="mut">{party.party_key}</span>{' '}
            <span className="pp-type">{party.party_type}</span>
            {party.processing_restricted ? <span className="pp-type restricted">restricted</span> : null}
          </li>
        ))}
        {hasLoadMore ? (
          <li
            id={optionId(shown.length)}
            role="option"
            className={`pp-more${active === shown.length ? ' active' : ''}`}
            aria-selected={active === shown.length}
          >
            <button
              type="button"
              className="btn sm"
              tabIndex={-1}
              onMouseDown={(event) => {
                event.preventDefault();
              }}
              onClick={loadMore}
            >
              Load more
            </button>
          </li>
        ) : null}
      </ul>
    </div>
  );
}
